import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientHeader from "../../components/Client/ClientHeader";
import Footer from "../../components/Client/Footer";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { createOrder } from "../../api/orderService";
import { trackUserEvent } from "../../api/userEventService";
import "../../components/Client/clientTheme.css";
import "./Checkout.css";

function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  return fallback;
}

const PAYMENT_METHODS = [
  { value: 1, titleKey: "cashOnDeliveryTitle", subtitleKey: "cashOnDeliverySubtitle" },
  { value: 2, titleKey: "cardPaymentTitle", subtitleKey: "cardPaymentSubtitle", disabled: true },
];

export default function Checkout() {
  const { t } = useTranslation(["checkout", "common"]);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { refresh } = useCart();

  const params = location.state;

  const [paymentMethod, setPaymentMethod] = useState(1);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [companySelections] = useState(() => params?.companySelections ?? {});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAuthenticated && params) trackUserEvent("CheckoutStarted");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAuthenticated || !params) {
    return <Navigate to="/cart" replace />;
  }

  const { items, selectedAddress, subtotal, deliveryFee: baseDeliveryFee, discountAmount, couponCode, deliveryQuote } = params;

  const storeGroups = [];
  const groupsByStoreId = new Map();
  items.forEach((item) => {
    let group = groupsByStoreId.get(item.storeId);
    if (!group) {
      group = { storeId: item.storeId, storeName: item.storeName, items: [] };
      groupsByStoreId.set(item.storeId, group);
      storeGroups.push(group);
    }
    group.items.push(item);
  });

  const primeStoreGroups = storeGroups
    .map((group) => ({ group, quote: deliveryQuote?.stores?.find((s) => s.storeId === group.storeId) }))
    .filter(({ quote }) => quote?.isPrime && quote.companies?.length);

  const selectedCompanyFor = (storeId, quote) => companySelections[storeId] ?? quote.companies[0].deliveryCompanyUserId;

  const deliveryFee =
    primeStoreGroups.length === 0
      ? baseDeliveryFee
      : storeGroups.reduce((sum, group) => {
          const quote = deliveryQuote?.stores?.find((s) => s.storeId === group.storeId);
          if (!quote) return sum;
          if (quote.isPrime && quote.companies?.length) {
            const companyId = selectedCompanyFor(group.storeId, quote);
            const company = quote.companies.find((c) => c.deliveryCompanyUserId === companyId);
            return sum + (company?.price ?? 0);
          }
          return sum + (quote.price ?? 0);
        }, 0);

  const specialDeliveryFee = storeGroups.reduce((sum, group) => {
    const quote = deliveryQuote?.stores?.find((s) => s.storeId === group.storeId);
    if (!quote) return sum;
    if (quote.isPrime && quote.companies?.length) {
      const companyId = selectedCompanyFor(group.storeId, quote);
      const company = quote.companies.find((c) => c.deliveryCompanyUserId === companyId);
      return sum + (company?.specialDeliveryFee ?? 0);
    }
    return sum + (quote.specialDeliveryFee ?? 0);
  }, 0);

  const total = Math.max(0, subtotal + deliveryFee - discountAmount);

  const addressLine = [selectedAddress.street, selectedAddress.buildingNumber, selectedAddress.cityNameEnglish || selectedAddress.city]
    .filter(Boolean)
    .join(", ");

  async function handleConfirm() {
    if (!paymentMethod || placingOrder) return;

    setPlacingOrder(true);
    setError(null);
    try {
      await createOrder({
        addressId: selectedAddress.addressId,
        paymentMethod,
        couponCode,
        deliverySelections: primeStoreGroups.map(({ group, quote }) => ({
          storeId: group.storeId,
          deliveryCompanyUserId: selectedCompanyFor(group.storeId, quote),
        })),
      });

      await refresh();
      navigate("/orders", { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, t("genericTryAgainMessage")));
    } finally {
      setPlacingOrder(false);
    }
  }

  return (
    <div className="client-checkout-page">
      <ClientHeader />

      <div className="client-checkout-content">
        <h1 className="client-checkout-title">{t("checkoutTitle")}</h1>

        <div className="client-checkout-layout">
          <div className="client-checkout-main">
            <section className="client-checkout-section">
              <h2 className="client-checkout-section-title">{t("orderSummary")}</h2>
              {storeGroups.map((group) => (
                <div key={group.storeId}>
                  <div className="client-checkout-store-header">{group.storeName}</div>
                  {group.items.map((item) => (
                    <div className="client-checkout-item-row" key={item.cartItemId}>
                      {item.productImageUrl ? (
                        <img src={item.productImageUrl} alt={item.productName} className="client-checkout-item-image" />
                      ) : (
                        <div className="client-checkout-item-image client-checkout-item-image-placeholder" />
                      )}
                      <div className="client-checkout-item-info">
                        <span className="client-checkout-item-name">{item.productName}</span>
                        {item.variantName && <span className="client-checkout-item-variant">{item.variantName}</span>}
                        <span className="client-checkout-item-qty">{t("qtyPrefix", { count: item.quantity })}</span>
                      </div>
                      <span className="client-checkout-item-price">{(item.unitPrice * item.quantity).toFixed(2)} ₪</span>
                    </div>
                  ))}
                </div>
              ))}
              {storeGroups.length > 1 && <p className="client-checkout-multi-store-note">{t("multiStoreNote")}</p>}
            </section>

            <section className="client-checkout-section">
              <div className="client-checkout-section-title-row">
                <h2 className="client-checkout-section-title">{t("deliveryAddress")}</h2>
                <Link to="/addresses" className="client-checkout-change-link">
                  {t("change")}
                </Link>
              </div>
              <div className="client-checkout-address-row">
                <span className="client-checkout-address-title">{selectedAddress.title || t("addressFallback")}</span>
                <span className="client-checkout-address-line">{addressLine}</span>
              </div>
            </section>

            {primeStoreGroups.map(({ group, quote }) => {
              const selectedCompanyId = selectedCompanyFor(group.storeId, quote);
              const selectedCompany = quote.companies.find((c) => c.deliveryCompanyUserId === selectedCompanyId);
              return (
                <section className="client-checkout-section" key={`delivery-company-${group.storeId}`}>
                  <div className="client-checkout-section-title-row">
                    <h2 className="client-checkout-section-title">
                      {storeGroups.length > 1 ? `${t("deliveryCompany")} — ${group.storeName}` : t("deliveryCompany")}
                    </h2>
                    <Link to="/cart" className="client-checkout-change-link">
                      {t("change")}
                    </Link>
                  </div>
                  <div className="client-checkout-address-row">
                    <span className="client-checkout-address-title">{selectedCompany?.companyName}</span>
                    <span className="client-checkout-item-price">{(selectedCompany?.price ?? 0).toFixed(2)} ₪</span>
                  </div>
                </section>
              );
            })}

            <section className="client-checkout-section">
              <h2 className="client-checkout-section-title">{t("paymentMethod")}</h2>
              {PAYMENT_METHODS.map((method) => {
                const selected = paymentMethod === method.value;
                return (
                  <button
                    type="button"
                    key={method.value}
                    className={`client-checkout-payment-option ${selected ? "selected" : ""} ${method.disabled ? "disabled" : ""}`}
                    onClick={() => !method.disabled && setPaymentMethod(method.value)}
                    disabled={method.disabled}
                  >
                    <div className="client-checkout-payment-info">
                      <div className="client-checkout-payment-title-row">
                        <span className="client-checkout-payment-title">{t(method.titleKey)}</span>
                        {method.disabled && <span className="client-checkout-coming-soon">{t("comingSoon")}</span>}
                      </div>
                      <span className="client-checkout-payment-subtitle">{t(method.subtitleKey)}</span>
                    </div>
                    {!method.disabled && <span className={`client-checkout-radio ${selected ? "checked" : ""}`} />}
                  </button>
                );
              })}
            </section>
          </div>

          <div className="client-checkout-summary">
            <h2 className="client-checkout-section-title">{t("total")}</h2>
            <div className="client-checkout-summary-row">
              <span>{t("subtotal")}</span>
              <span>{subtotal.toFixed(2)} ₪</span>
            </div>
            <div className="client-checkout-summary-row">
              <span>{t("delivery")}</span>
              <span>{deliveryFee.toFixed(2)} ₪</span>
            </div>
            {specialDeliveryFee > 0 && (
              <div className="client-checkout-summary-row">
                <span>{t("specialDeliveryFee")}</span>
                <span>{specialDeliveryFee.toFixed(2)} ₪</span>
              </div>
            )}
            {discountAmount > 0 && (
              <div className="client-checkout-summary-row">
                <span>{t("discount")}</span>
                <span className="client-checkout-discount">-{discountAmount.toFixed(2)} ₪</span>
              </div>
            )}
            <div className="client-checkout-summary-row total">
              <span>{t("total")}</span>
              <span>{total.toFixed(2)} ₪</span>
            </div>

            {error && <p className="client-checkout-error">{error}</p>}

            <button
              type="button"
              className="client-checkout-confirm-button"
              onClick={handleConfirm}
              disabled={!paymentMethod || placingOrder}
            >
              {placingOrder ? t("common:loading") : t("confirmOrder")}
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
